import React, { PropsWithChildren, useEffect, useState } from 'react';
import { ITableChangeState } from 'react-bootstrap-table-next';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { DataGrid, IDataGridProps } from './DataGrid';
import {
  IFilteredDataGridColumnProps,
  IFilterValues,
  MultiPieceSearchBar,
  initializeFiltersState,
} from './MultiFieldSearchBar';

export interface IFilteredDataGridProps<T extends object = {}> extends IDataGridProps<T>, IHasTestData {
  columns: IFilteredDataGridColumnProps<T>[];
}

/** Things to watch out for:
 * Column headers determine the minimum width of a column, so if the filters don't line up, see if a header is too big for the size you gave it
 * Each column needs a width when using this.
 * The last column can not be filtered on as is because it is used for the search button
 */
export const FilteredDataGrid = <T extends object = {}>({
  onTableChange,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
  ...rest
}: PropsWithChildren<IFilteredDataGridProps<T>>) => {
  const [sendOnTableChangeEvent, setSendOnTableChangeEvent] = useState(false);
  const [filters, setFilters] = useState<IFilterValues>(initializeFiltersState(rest.columns));
  const [eventType, setEventType] = useState<'cellEdit' | 'filter' | 'pagination' | 'sort'>('pagination');
  // Eventually default data will come along and this will change to a useEffect, or default values.
  const [tableState, setTableState] = useState<ITableChangeState<T>>({
    page: (rest.pagination && rest.pagination.page) || 1,
    sizePerPage: (rest.pagination && rest.pagination.sizePerPage) || 10,
    filters: {},
    data: rest.data || [],
  });

  //Update state when props change
  useEffect(() => {
    setTableState((currentTableState) => ({
      ...currentTableState,
      page: (rest.pagination && rest.pagination.page) || 1,
      sizePerPage: (rest.pagination && rest.pagination.sizePerPage) || 10,
      data: rest.data || [],
    }));
  }, [rest.pagination, rest.data]);

  useEffect(() => {
    setFilters(initializeFiltersState(rest.columns));
  }, [rest.columns]);

  useEffect(() => {
    if (sendOnTableChangeEvent && onTableChange) {
      onTableChange(eventType, { ...tableState, filters });
      setSendOnTableChangeEvent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOnTableChangeEvent, eventType, filters, tableState]);

  return (
    <>
      <MultiPieceSearchBar
        onSearch={(newFilters) => {
          setEventType('filter');
          setFilters(newFilters);
          setTableState({ ...tableState, filters: newFilters, page: 1 });
          setSendOnTableChangeEvent(true);
        }}
        columns={rest.columns}
        data-test-id={dataTestId}
        data-test-class={dataTestClass || 'data-grid-search-bar'}
      />
      <DataGrid
        {...rest}
        remote={true}
        data-test-class={dataTestClass || 'data-grid'}
        onTableChange={(type, newState) => {
          setEventType(type);
          setTableState(newState);
          setSendOnTableChangeEvent(true);
        }}
      />
    </>
  );
};
